import type {
  LargeOperation,
  ParameterValueCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { useMemo } from 'react'

import { useMetaModelContext } from '../context/metaModelContext'
import { useNavigationParams } from '../system/useNavigationParams'
import type { OperationWithSourceInfo } from '../uiModels/types'
import { getApplicationViewOperations } from './helpers'
import { useOperationsContext } from './operationsContext'

export const useApplicationViewParameterValues = () => {
  const { layer, block } = useNavigationParams()
  const { parameterValueEntityOperations } = useOperationsContext()
  const { layerTopology, blockTopology } = useMetaModelContext()

  return useMemo(
    () =>
      getApplicationViewOperations({
        operations: parameterValueEntityOperations,
        layer,
        block,
        layerTopology,
        blockTopology,
        isActiveAtViewDate: () => true,
        combineBlockOperations: ({
          baseOperation: evaluatedBaseOperation,
          newOperation: evaluatedNewOperation,
        }) => {
          const baseOperations = evaluatedBaseOperation?.filter(
            (operation) => !operation.annulOperations?.length,
          )
          const newOperations = evaluatedNewOperation?.filter(
            (operation) => !operation.annulOperations?.length,
          )

          const cloneOperation = (
            operation: OperationWithSourceInfo<
              ParameterValueCreateOperation | LargeOperation
            >,
          ) => ({
            ...operation,
            isDraft: operation.isDraft && operation.block === block,
            layer: new LayerKey(operation.layer),
          })

          const nextOperations = baseOperations?.map(cloneOperation) ?? []

          nextOperations.push(...(newOperations ?? []).map(cloneOperation))

          return nextOperations.length ? nextOperations : undefined
        },
      }),
    [
      parameterValueEntityOperations,
      layer,
      block,
      layerTopology,
      blockTopology,
    ],
  )
}
